import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Grid,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Refresh,
  BookOnline,
  CardMembership,
  CancelScheduleSend,
  AcUnit,
  CurrencyRupee,
  Warning,
  CheckCircle,
  HelpOutline,
  FitnessCenter,
  Spa,
  ShoppingBag,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';

// ── Types (mirror backend payload) ─────────────────────────────────────────
interface UserRef { name?: string; phone?: string; email?: string }
interface BookingRow {
  bookingId: string;
  status: string;
  category: 'pack' | 'trial' | 'dropin' | 'event';
  modeOfPayment?: string | null;
  isWalkIn: boolean;
  isDuetBooking: boolean;
  user?: UserRef | null;
  item: string;
  sessionType?: string | null;
  bookedAtIST?: string | null;
  sessionTimeIST?: string | null;
  amount: number;
}
interface CancelRow {
  bookingId: string;
  type: string;
  reason?: string | null;
  user?: UserRef | null;
  item: string;
  sessionTimeIST?: string | null;
  cancelledAtIST?: string | null;
  cancelledAtIsExact: boolean;
  hoursBeforeSession?: number | null;
  withinCutoff: boolean;
  cutoffHours: number;
  creditRestored?: boolean | null;
}
interface PackPurchaseRow {
  packTitle: string;
  totalClasses?: number | null;
  user?: UserRef | null;
  amountPaid: number;
  basePrice: number;
  gst: number;
  modeOfPayment?: string;
  purchasedAtIST?: string | null;
  expiryIST?: string | null;
}
interface FreezeRow {
  packTitle: string;
  user?: UserRef | null;
  isFrozen: boolean;
  scheduled: boolean;
  frozenSinceIST?: string | null;
  scheduledUnfreezeIST?: string | null;
  currentExpiryIST?: string | null;
  totalFreezeDaysUsed: number;
  adminAdjustmentDays: number;
  freezeWindows: { startIST?: string | null; endIST?: string | null; daysUsed: number; month?: string | null; open: boolean; isAdjustment: boolean }[];
  newInPeriod: boolean;
  bookingsDuringFreeze: number;
}
interface Overview {
  studio: { id: string; name: string };
  range: 'day' | 'week';
  dateIST: string;
  periodIST: { start: string; end: string };
  generatedAtIST: string;
  policy: { sessionCutoffHours: number; eventCutoffHours: number };
  summary: {
    bookingsMade: {
      total: number;
      pack: { count: number; revenue: number };
      trial: { count: number; revenue: number };
      dropIn: { count: number; revenue: number };
      event: { count: number; revenue: number };
      walkIn: { count: number };
      free: { count: number };
    };
    cancellations: {
      total: number;
      withinCutoff: number;
      outsideCutoff: number;
      creditRestored: number;
      noCreditRestored: number;
      creditUnknown: number;
    };
    packPurchases: { count: number; revenue: number; base: number; gst: number };
    freezes: { activeFreezes: number; newFreezesInPeriod: number; anomalies: number };
    revenueTotalInPeriod: number;
    categorySplit?: {
      total: { count: number; revenue: number };
      workout: { count: number; revenue: number };
      wellness?: { count: number; revenue: number };
      merchandise?: { count: number; revenue: number };
    };
  };
  bookings: BookingRow[];
  cancellations: CancelRow[];
  packPurchases: PackPurchaseRow[];
  freezes: FreezeRow[];
}

const PP_PURPLE = '#863B79';

// IST-correct calendar date string regardless of the admin's browser timezone.
function istDateStr(offsetDays = 0): string {
  const istNow = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }),
  );
  istNow.setDate(istNow.getDate() + offsetDays);
  const y = istNow.getFullYear();
  const m = String(istNow.getMonth() + 1).padStart(2, '0');
  const d = String(istNow.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const rupee = (n: number) => `₹${(n ?? 0).toLocaleString('en-IN')}`;

type Preset = 'today' | 'yesterday' | 'week' | 'custom';

const categoryColor: Record<string, string> = {
  pack: '#6A1B9A',
  trial: '#EF6C00',
  dropin: '#1565C0',
  event: '#2E7D32',
};

interface StudioOption { _id: string; studioName: string }

// Default selection on first load (Purple Pilates). The page works for ANY studio.
const DEFAULT_STUDIO_ID = '6881bed3fe40745d69d49817';

const PurplePilatesOpsPage: React.FC = () => {
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<Preset>('today');
  const [customDate, setCustomDate] = useState<string>(istDateStr());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastFetched, setLastFetched] = useState<string>('');
  const [studios, setStudios] = useState<StudioOption[]>([]);
  const [selectedStudio, setSelectedStudio] = useState<StudioOption | null>(null);

  // Load the studio list once (for the picker) and default-select Purple Pilates.
  useEffect(() => {
    (async () => {
      try {
        const res: any = await apiService.getStudios();
        const list: StudioOption[] = (res?.data || res?.studios || res || [])
          .map((x: any) => ({ _id: x._id, studioName: x.studioName }))
          .filter((x: StudioOption) => x._id && x.studioName);
        setStudios(list);
        setSelectedStudio(
          list.find((x) => x._id === DEFAULT_STUDIO_ID) || list[0] || null,
        );
      } catch {
        // Picker stays empty; the endpoint still defaults to PP server-side.
      }
    })();
  }, []);

  const { range, date } = useMemo<{ range: 'day' | 'week'; date: string }>(() => {
    switch (preset) {
      case 'yesterday':
        return { range: 'day', date: istDateStr(-1) };
      case 'week':
        return { range: 'week', date: istDateStr() };
      case 'custom':
        return { range: 'day', date: customDate };
      default:
        return { range: 'day', date: istDateStr() };
    }
  }, [preset, customDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getStudioOverview({
        range,
        date,
        studioId: selectedStudio?._id,
      });
      setData(res as Overview);
      setLastFetched(new Date().toLocaleTimeString('en-IN'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, [range, date, selectedStudio]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchData, 60_000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchData]);

  const s = data?.summary;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: PP_PURPLE }}>
            {data?.studio.name ? `${data.studio.name} — Operations` : 'Studio Operations'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data
              ? `${data.range === 'week' ? `Week ${data.periodIST.start} → ${data.periodIST.end}` : data.dateIST} (IST) · generated ${data.generatedAtIST}`
              : 'Loading…'}
            {lastFetched ? ` · refreshed ${lastFetched}` : ''}
          </Typography>
        </Box>
        <Autocomplete
          size="small"
          options={studios}
          getOptionLabel={(o) => o.studioName}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          value={selectedStudio}
          onChange={(_, v) => setSelectedStudio(v)}
          renderInput={(params) => <TextField {...params} label="Studio" placeholder="Select studio" />}
          sx={{ minWidth: 220 }}
        />
        <ButtonGroup variant="outlined" size="small">
          {(['today', 'yesterday', 'week'] as Preset[]).map((p) => (
            <Button
              key={p}
              onClick={() => setPreset(p)}
              variant={preset === p ? 'contained' : 'outlined'}
              sx={preset === p ? { bgcolor: PP_PURPLE, '&:hover': { bgcolor: PP_PURPLE } } : { color: PP_PURPLE, borderColor: PP_PURPLE }}
            >
              {p === 'today' ? 'Today' : p === 'yesterday' ? 'Yesterday' : 'This Week'}
            </Button>
          ))}
        </ButtonGroup>
        <TextField
          type="date"
          size="small"
          label="Date"
          InputLabelProps={{ shrink: true }}
          value={customDate}
          onChange={(e) => {
            setCustomDate(e.target.value);
            setPreset('custom');
          }}
        />
        <Tooltip title={autoRefresh ? 'Auto-refresh ON (60s)' : 'Auto-refresh OFF'}>
          <Button size="small" variant={autoRefresh ? 'contained' : 'outlined'} onClick={() => setAutoRefresh((v) => !v)}
            sx={autoRefresh ? { bgcolor: PP_PURPLE, '&:hover': { bgcolor: PP_PURPLE } } : {}}>
            Live
          </Button>
        </Tooltip>
        <IconButton onClick={fetchData} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : <Refresh />}
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards */}
      {s && (
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <SummaryCard
            icon={<CurrencyRupee />}
            title="Revenue (period)"
            value={rupee(s.revenueTotalInPeriod)}
            sub={`Packs ${rupee(s.packPurchases.revenue)} · Drop-in ${rupee(s.bookingsMade.dropIn.revenue)} · Trials ${rupee(s.bookingsMade.trial.revenue)} · Events ${rupee(s.bookingsMade.event.revenue)}`}
            color={PP_PURPLE}
          />
          <SummaryCard
            icon={<BookOnline />}
            title="Bookings made"
            value={String(s.bookingsMade.total)}
            sub={`Pack ${s.bookingsMade.pack.count} · Drop-in ${s.bookingsMade.dropIn.count} · Trial ${s.bookingsMade.trial.count} · Event ${s.bookingsMade.event.count} · Walk-in ${s.bookingsMade.walkIn.count}`}
            color="#1565C0"
          />
          <SummaryCard
            icon={<CardMembership />}
            title="Pack purchases"
            value={String(s.packPurchases.count)}
            sub={`${rupee(s.packPurchases.revenue)}  (base ${rupee(s.packPurchases.base)} + GST ${rupee(s.packPurchases.gst)})`}
            color="#6A1B9A"
          />
          <SummaryCard
            icon={<CancelScheduleSend />}
            title="Cancellations"
            value={String(s.cancellations.total)}
            sub={`Credit back ${s.cancellations.creditRestored} · no credit ${s.cancellations.noCreditRestored} · within cutoff ${s.cancellations.withinCutoff}`}
            color={s.cancellations.withinCutoff > 0 ? '#C62828' : '#2E7D32'}
          />
          <SummaryCard
            icon={<AcUnit />}
            title="Freezes"
            value={`${s.freezes.activeFreezes} active`}
            sub={`New ${s.freezes.newFreezesInPeriod} · anomalies ${s.freezes.anomalies}`}
            color={s.freezes.anomalies > 0 ? '#C62828' : '#00838F'}
          />
        </Grid>
      )}

      {/* Category split — total + independent workout / wellness / merchandise.
          Shown only for multi-category studios (wellness and/or merch enabled). */}
      {s?.categorySplit && (s.categorySplit.wellness || s.categorySplit.merchandise) && (
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <SummaryCard
            icon={<CurrencyRupee />}
            title="Total (all categories)"
            value={rupee(s.categorySplit.total.revenue)}
            sub={`${s.categorySplit.total.count} bookings/orders`}
            color={PP_PURPLE}
          />
          <SummaryCard
            icon={<FitnessCenter />}
            title="Workout"
            value={rupee(s.categorySplit.workout.revenue)}
            sub={`${s.categorySplit.workout.count} bookings`}
            color="#1565C0"
          />
          {s.categorySplit.wellness && (
            <SummaryCard
              icon={<Spa />}
              title="Wellness"
              value={rupee(s.categorySplit.wellness.revenue)}
              sub={`${s.categorySplit.wellness.count} bookings`}
              color="#2E7D32"
            />
          )}
          {s.categorySplit.merchandise && (
            <SummaryCard
              icon={<ShoppingBag />}
              title="Merchandise"
              value={rupee(s.categorySplit.merchandise.revenue)}
              sub={`${s.categorySplit.merchandise.count} orders`}
              color="#EF6C00"
            />
          )}
        </Grid>
      )}

      {/* Cutoff banner */}
      {data && (
        <Alert
          severity={s && s.cancellations.withinCutoff > 0 ? 'warning' : 'info'}
          icon={s && s.cancellations.withinCutoff > 0 ? <Warning /> : <CheckCircle />}
          sx={{ mb: 2 }}
        >
          Cancellation cutoff: sessions <b>{data.policy.sessionCutoffHours}h</b>, events <b>{data.policy.eventCutoffHours}h</b>.{' '}
          {s && s.cancellations.withinCutoff > 0
            ? `${s.cancellations.withinCutoff} cancellation(s) happened INSIDE the cutoff window — review below.`
            : 'No cancellations breached the cutoff window.'}
          {s && s.freezes.anomalies > 0 && ` · ${s.freezes.anomalies} pack(s) have a booking inside a freeze window — review Freezes.`}
        </Alert>
      )}

      {/* Bookings table */}
      <SectionTable title={`Bookings made (${data?.bookings.length ?? 0})`}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Booking', 'User', 'Item', 'Type', 'Booked (IST)', 'Session (IST)', 'Amount'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.bookings ?? []).map((b) => (
              <TableRow key={b.bookingId} hover>
                <TableCell>#{b.bookingId}{b.isWalkIn && <Chip size="small" label="walk-in" sx={{ ml: 0.5, height: 18 }} />}</TableCell>
                <TableCell>{b.user?.name ?? '—'}<br /><Typography variant="caption" color="text.secondary">{b.user?.phone ?? ''}</Typography></TableCell>
                <TableCell>{b.item}{b.sessionType && b.sessionType !== 'group' ? ` · ${b.sessionType}` : ''}</TableCell>
                <TableCell><Chip size="small" label={b.category} sx={{ bgcolor: categoryColor[b.category], color: '#fff', textTransform: 'capitalize' }} /></TableCell>
                <TableCell>{b.bookedAtIST ?? '—'}</TableCell>
                <TableCell>{b.sessionTimeIST ?? '—'}</TableCell>
                <TableCell>{b.category === 'pack' ? <em>pack</em> : rupee(b.amount)}</TableCell>
              </TableRow>
            ))}
            {!loading && (data?.bookings.length ?? 0) === 0 && <EmptyRow cols={7} />}
          </TableBody>
        </Table>
      </SectionTable>

      {/* Cancellations table */}
      <SectionTable title={`Cancellations (${data?.cancellations.length ?? 0})`}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Booking', 'User', 'Item', 'Type', 'Session (IST)', 'Cancelled (IST)', 'Hrs before', 'Cutoff', 'Credit back'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.cancellations ?? []).map((c) => (
              <TableRow key={c.bookingId} hover sx={c.withinCutoff ? { bgcolor: '#FFF3F3' } : undefined}>
                <TableCell>#{c.bookingId}</TableCell>
                <TableCell>{c.user?.name ?? '—'}<br /><Typography variant="caption" color="text.secondary">{c.user?.phone ?? ''}</Typography></TableCell>
                <TableCell>{c.item}{c.reason ? <><br /><Typography variant="caption" color="text.secondary">{c.reason}</Typography></> : null}</TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{c.type}</TableCell>
                <TableCell>{c.sessionTimeIST ?? '—'}</TableCell>
                <TableCell>
                  {c.cancelledAtIST ?? '—'}
                  {!c.cancelledAtIsExact && <Tooltip title="Derived from updatedAt (cancelled before audit field existed)"><HelpOutline sx={{ fontSize: 13, ml: 0.5, color: '#999' }} /></Tooltip>}
                </TableCell>
                <TableCell>{c.hoursBeforeSession != null ? `${c.hoursBeforeSession}h` : '—'}</TableCell>
                <TableCell>
                  {c.withinCutoff
                    ? <Chip size="small" label={`inside ${c.cutoffHours}h`} sx={{ bgcolor: '#C62828', color: '#fff' }} />
                    : <Chip size="small" label="ok" sx={{ bgcolor: '#2E7D32', color: '#fff' }} />}
                </TableCell>
                <TableCell>
                  {c.creditRestored === true ? <Chip size="small" color="success" label="yes" />
                    : c.creditRestored === false ? <Chip size="small" label="no" />
                      : <Chip size="small" variant="outlined" label="n/a" />}
                </TableCell>
              </TableRow>
            ))}
            {!loading && (data?.cancellations.length ?? 0) === 0 && <EmptyRow cols={9} />}
          </TableBody>
        </Table>
      </SectionTable>

      {/* Pack purchases table */}
      <SectionTable title={`Pack purchases (${data?.packPurchases.length ?? 0})`}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Pack', 'User', 'Classes', 'Paid', 'Base', 'GST', 'Mode', 'Purchased (IST)', 'Expiry (IST)'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.packPurchases ?? []).map((p, i) => (
              <TableRow key={i} hover>
                <TableCell>{p.packTitle}</TableCell>
                <TableCell>{p.user?.name ?? '—'}<br /><Typography variant="caption" color="text.secondary">{p.user?.phone ?? ''}</Typography></TableCell>
                <TableCell>{p.totalClasses ?? 'Unlimited'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{rupee(p.amountPaid)}</TableCell>
                <TableCell>{rupee(p.basePrice)}</TableCell>
                <TableCell>{rupee(p.gst)}</TableCell>
                <TableCell>{p.modeOfPayment}</TableCell>
                <TableCell>{p.purchasedAtIST ?? '—'}</TableCell>
                <TableCell>{p.expiryIST ?? '—'}</TableCell>
              </TableRow>
            ))}
            {!loading && (data?.packPurchases.length ?? 0) === 0 && <EmptyRow cols={9} />}
          </TableBody>
        </Table>
      </SectionTable>

      {/* Freezes table */}
      <SectionTable title={`Freezes (${data?.freezes.length ?? 0})`}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Pack', 'User', 'State', 'Frozen since (IST)', 'Unfreeze (IST)', 'Current expiry (IST)', 'Days used', 'Bookings in window'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.freezes ?? []).map((f, i) => (
              <TableRow key={i} hover sx={f.bookingsDuringFreeze > 0 ? { bgcolor: '#FFF3F3' } : undefined}>
                <TableCell>{f.packTitle}</TableCell>
                <TableCell>{f.user?.name ?? '—'}<br /><Typography variant="caption" color="text.secondary">{f.user?.phone ?? ''}</Typography></TableCell>
                <TableCell>
                  {f.isFrozen ? <Chip size="small" label="frozen" sx={{ bgcolor: '#00838F', color: '#fff' }} />
                    : f.scheduled ? <Chip size="small" label="scheduled" sx={{ bgcolor: '#6D28D9', color: '#fff' }} />
                      : <Chip size="small" variant="outlined" label="past" />}
                  {f.newInPeriod && <Chip size="small" label="new" sx={{ ml: 0.5 }} />}
                </TableCell>
                <TableCell>{f.frozenSinceIST ?? '—'}</TableCell>
                <TableCell>{f.scheduledUnfreezeIST ?? '—'}</TableCell>
                <TableCell>{f.currentExpiryIST ?? '—'}</TableCell>
                <TableCell>
                  {f.totalFreezeDaysUsed}
                  {f.adminAdjustmentDays !== 0 && (
                    <Tooltip title="Admin override of remaining freeze days (negative = days granted to the user)">
                      <Chip
                        size="small"
                        label={`admin ${f.adminAdjustmentDays > 0 ? '+' : ''}${f.adminAdjustmentDays}`}
                        sx={{ ml: 0.5, height: 18, bgcolor: '#ECEFF1' }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  {f.bookingsDuringFreeze > 0
                    ? <Chip size="small" icon={<Warning sx={{ color: '#fff !important' }} />} label={String(f.bookingsDuringFreeze)} sx={{ bgcolor: '#C62828', color: '#fff' }} />
                    : <Chip size="small" color="success" label="0" />}
                </TableCell>
              </TableRow>
            ))}
            {!loading && (data?.freezes.length ?? 0) === 0 && <EmptyRow cols={8} />}
          </TableBody>
        </Table>
      </SectionTable>
    </Box>
  );
};

// ── Small presentational helpers ────────────────────────────────────────────
const SummaryCard: React.FC<{ icon: React.ReactNode; title: string; value: string; sub?: string; color: string }> = ({ icon, title, value, sub, color }) => (
  <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
    <Card sx={{ height: '100%', borderTop: `3px solid ${color}` }}>
      <CardContent sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color }}>
          {icon}
          <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  </Grid>
);

const SectionTable: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper sx={{ mb: 2, overflow: 'hidden' }}>
    <Box sx={{ px: 2, py: 1.25, bgcolor: '#FAF6F9' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
    </Box>
    <Divider />
    <TableContainer sx={{ maxHeight: 460 }}>{children}</TableContainer>
  </Paper>
);

const EmptyRow: React.FC<{ cols: number }> = ({ cols }) => (
  <TableRow>
    <TableCell colSpan={cols} align="center" sx={{ py: 3, color: 'text.secondary' }}>
      Nothing in this period.
    </TableCell>
  </TableRow>
);

export default PurplePilatesOpsPage;
